import React, { useState, useCallback, useEffect } from 'react';
import moment from 'moment';
import { Input } from 'reactstrap';
import _ from 'underscore';
import { Tooltip } from 'react-tooltip';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useActions } from 'hooks/useActions';
import { SearchSelect } from 'components/SearchSelect/SearchSelect';
import TicketLinkModal from 'components/Modals/TicketModals/TicketLinkModal';
import { userRoles } from 'constants/users';
import { categories } from 'constants/tests';
import { Images } from 'assets/images';
import { Spinner } from 'components/Spinner/Spinner';
import { colors } from 'global/colors';
import YesNoModal from 'components/Modals/yesNoModal';
import Pagination from 'components/Pagination/Pagination';
import ResultBar from './resultBar';
import { calculatePercentage } from 'functions/testResult';
import { downloadReport } from 'functions/downloadReport';
import { RectangleButton } from 'components/Button/RectangleButton';
import * as actions from '../actions';
import {
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableHeading,
  TableHeader,
  TableData,
  SortArrowImage,
} from 'components/CMSRankList/styles';
import { ITag } from '../styles';
import { TimeBox, StatusLabel, TestSelectContainer, TestInformationText, ClearFilterText } from './styles';

export default function TicketsTable({ user, tickets, testsAvailableForCompany, loading }) {
  const localStorageInfo = JSON.parse(localStorage.getItem('fsTestsPageFilter'));
  const navigate = useNavigate();
  const itemsPerPage = 15;

  const [candidateName, setCandidateName] = useState(localStorageInfo?.candidateName ?? '');
  const [companyName, setCompanyName] = useState(localStorageInfo?.companyName ?? '');
  const [departmentName, setDepartmentName] = useState(localStorageInfo?.departmentName ?? '');
  const [testName, setTestName] = useState(localStorageInfo?.testName ?? '');
  const [customerName, setCustomerName] = useState(localStorageInfo?.customerName ?? '');
  const [date, setDate] = useState(localStorageInfo?.date ?? '');
  const [ticketsList, setTicketsList] = useState([]);
  const [paginatedItems, setPaginatedItems] = useState([]);
  const [testsList, setTestsList] = useState([]);
  const [sortBy, setSortBy] = useState({
    value: localStorageInfo?.sortBy ?? 'createdAt',
    isAscending: localStorageInfo?.isAscending ?? false,
  });
  const [showYesNoModal, setShowYesNoModal] = useState(false);
  const [yesNoModalInfo, setYesNoModalInfo] = useState({});
  const [showTicketLinkModal, setShowTicketLinkModal] = useState(false);
  const [ticketModalInfo, setTicketModalInfo] = useState([]);
  const [isPdfDownloading, setIsPdfDownloading] = useState(false);
  const [version, setVersion] = useState(0);

  const { archiveTicket } = useActions(actions);

  // Returns a function that takes a ticket paramter so it can be used as filter for tickets
  const searchQueryFilter = useCallback(
    () => (ticket) => {
      const notIncluding = (ticketValue, filterValue) =>
        filterValue && String(ticketValue).toLowerCase().indexOf(String(filterValue).toLowerCase()) < 0;

      // candidateName can be in name or email
      if (notIncluding(ticket.candidateName, candidateName) && notIncluding(ticket.candidateEmail, candidateName))
        return false;

      if (notIncluding(ticket.companyName, companyName)) return false;
      if (notIncluding(ticket.departmentName, departmentName)) return false;
      if (notIncluding(ticket.testName, testName)) return false;
      if (notIncluding(ticket.customerName, customerName)) return false;
      if (date && !moment(ticket.completedAt || ticket.startedAt || ticket.createdAt).isSame(moment(date), 'day'))
        return false;

      return true;
    },
    [candidateName, companyName, customerName, departmentName, testName, date]
  );

  const sortFunc = useCallback(
    (a, b) => {
      let desc = 1;
      let asc = -1;

      if (sortBy.value === 'status') {
        // TODO Is this date sorting correct?
        if (sortBy.isAscending) {
          if (a.startedAt && b.startedAt) {
            return new Date(a.startedAt) - new Date(b.startedAt);
          }
          if (!a.startedAt && !b.startedAt) {
            return new Date(a.deadlineAt) - new Date(b.deadlineAt);
          }
          return a.startedAt ? desc : asc;
        } else {
          if (a.startedAt && b.startedAt) {
            return new Date(b.startedAt) - new Date(a.startedAt);
          }
          if (!a.startedAt && !b.startedAt) {
            return new Date(b.deadlineAt) - new Date(a.deadlineAt);
          }
          return a.startedAt ? asc : desc;
        }
      }

      const getScore = (ticket) => {
        if (!ticket.startedAt) return -2;
        if (!ticket.completedAt || ticket.processing) return -1;
        let test = _.findWhere(testsAvailableForCompany, { _id: ticket.test });
        return calculatePercentage(test, ticket, false, true);
      };

      if (sortBy.value === 'result') {
        const scoreA = getScore(a);
        const scoreB = getScore(b);
        return sortBy.isAscending ? scoreA - scoreB : scoreB - scoreA;
      }

      let sortByValueOfA = sortBy.value;
      let sortByValueOfB = sortBy.value;
      if (sortBy.value === 'createdAt') {
        sortByValueOfA = a.deadlineAt ? 'deadlineAt' : 'createdAt';
        sortByValueOfB = b.deadlineAt ? 'deadlineAt' : 'createdAt';
      }

      if (String(a[sortByValueOfA]).toLowerCase() > String(b[sortByValueOfB]).toLowerCase()) {
        return sortBy.isAscending ? desc : asc;
      }
      if (String(a[sortByValueOfA]).toLowerCase() < String(b[sortByValueOfB]).toLowerCase()) {
        return sortBy.isAscending ? asc : desc;
      }
      return 0;
    },
    [sortBy, testsAvailableForCompany]
  );

  useEffect(() => {
    let uniqueTestNames = _.uniq(_.pluck(tickets, 'testName'));
    let tests = uniqueTestNames.map((t) => {
      return { label: t, value: t };
    });
    tests.sort((a, b) => {
      if (a.value > b.value) return 1;
      if (a.value < b.value) return -1;
      return 0;
    });
    setTestsList(tests);
  }, [tickets]);

  useEffect(() => {
    let filteredTickets = tickets.filter(searchQueryFilter());
    setTicketsList(filteredTickets);
    if (filteredTickets.length <= itemsPerPage) setPaginatedItems(filteredTickets.sort(sortFunc));
  }, [searchQueryFilter, sortFunc, tickets]);

  useEffect(() => {
    // Updating the version which is used for key in the pagination forces a new component to be created.
    // which will reset the pagination https://react.dev/reference/react/useState#resetting-state-with-a-key
    setVersion((version) => {
      return version + 1;
    });
  }, [sortBy, candidateName, companyName, departmentName, testName, customerName, date]);

  const statusText = (ticket) => {
    if (ticket.processing) return 'Processing result';
    if (ticket.completedAt) return 'Finished';
    if (ticket.startedAt) return 'Started';
    return 'Not started';
  };

  const updateLocalStorageInfo = (newInfo) => {
    let oldInfo = JSON.parse(localStorage.getItem('fsTestsPageFilter')) ?? {};
    localStorage.setItem('fsTestsPageFilter', JSON.stringify({ ...oldInfo, ...newInfo }));
  };

  const updateTestName = (testName) => {
    setTestName(testName);
    updateLocalStorageInfo({ testName });
  };

  const updateSortBy = (field) => {
    updateLocalStorageInfo({ sortBy: field, isAscending: !sortBy.isAscending });
    setSortBy({ value: field, isAscending: !sortBy.isAscending });
  };

  const clearAllFilters = () => {
    setCandidateName('');
    setCompanyName('');
    setDepartmentName('');
    setTestName('');
    setCustomerName('');
    setDate('');
    setSortBy({
      value: 'createdAt',
      isAscending: false,
    });
    localStorage.removeItem('fsTestsPageFilter');
  };

  const isClearFilterVisible = () => {
    if (localStorageInfo?.pageNumber) return true;
    if (
      candidateName === '' &&
      companyName === '' &&
      testName === '' &&
      departmentName === '' &&
      customerName === '' &&
      date === '' &&
      sortBy.value === 'createdAt' &&
      sortBy.isAscending === false
    )
      return false;
    return true;
  };

  const renderTableHeading = () => {
    return (
      <TableRow isHeading>
        <TableHeading>
          <TableHeader>
            <Input
              type="text"
              placeholder="Name"
              value={candidateName}
              onChange={(e) => {
                updateLocalStorageInfo({ candidateName: e.target.value });
                setCandidateName(e.target.value);
              }}
            />
            <SortArrowImage
              alt="arrow"
              src={sortBy.isAscending ? Images.downArrow : Images.upArrow}
              onClick={() => updateSortBy('candidateName')}
            />
          </TableHeader>
        </TableHeading>
        {user.viewAll && (
          <TableHeading hideOnSmallDevice hideOnMobileDevice>
            <TableHeader>
              <Input
                type="text"
                value={companyName}
                placeholder="Company"
                onChange={(e) => {
                  updateLocalStorageInfo({ companyName: e.target.value });
                  setCompanyName(e.target.value);
                }}
              />
              <SortArrowImage
                alt="arrow"
                src={sortBy.isAscending ? Images.downArrow : Images.upArrow}
                onClick={() => updateSortBy('companyName')}
              />
            </TableHeader>
          </TableHeading>
        )}
        {user.role === userRoles.COMPANY_ADMIN && user.viewAllDepartments && (
          <TableHeading hideOnSmallDevice hideOnMobileDevice>
            <TableHeader>
              <Input
                type="text"
                value={departmentName}
                placeholder="Department"
                onChange={(e) => {
                  updateLocalStorageInfo({ departmentName: e.target.value });
                  setDepartmentName(e.target.value);
                }}
              />
              <SortArrowImage
                alt="arrow"
                src={sortBy.isAscending ? Images.downArrow : Images.upArrow}
                onClick={() => updateSortBy('departmentName')}
              />
            </TableHeader>
          </TableHeading>
        )}
        <TableHeading hideOnMobileDevice>
          <TableHeader>
            <TestSelectContainer>
              <SearchSelect
                options={testsList}
                isClearable={true}
                isSearchable={true}
                placeholder="Tests"
                defaultInputValue={localStorageInfo?.testName}
                onInputChange={(input) => updateTestName(input)}
                onMenuClose={() => updateTestName(testName)}
                onChange={(response) => updateTestName(response?.label ?? '')}
              />
              <SortArrowImage
                alt="arrow"
                src={sortBy.isAscending ? Images.downArrow : Images.upArrow}
                onClick={() => updateSortBy('testName')}
              />
            </TestSelectContainer>
          </TableHeader>
        </TableHeading>
        <TableHeading hideOnSmallDevice hideOnMobileDevice>
          <TableHeader>
            <Input
              type="text"
              value={customerName}
              placeholder="Owner"
              onChange={(e) => {
                updateLocalStorageInfo({ customerName: e.target.value });
                setCustomerName(e.target.value);
              }}
            />
            <SortArrowImage
              alt="arrow"
              src={sortBy.isAscending ? Images.downArrow : Images.upArrow}
              onClick={() => updateSortBy('customerName')}
            />
          </TableHeader>
        </TableHeading>
        <TableHeading hideOnSmallDevice hideOnMobileDevice>
          <TableHeader>
            <Input
              type="date"
              value={date}
              onChange={(e) => {
                updateLocalStorageInfo({ date: e.target.value });
                setDate(e.target.value);
              }}
            />
            <SortArrowImage
              alt="arrow"
              src={sortBy.isAscending ? Images.downArrow : Images.upArrow}
              onClick={() => updateSortBy('createdAt')}
            />
          </TableHeader>
        </TableHeading>
        <TableHeading hideOnMobileDevice>
          Status
          <SortArrowImage
            alt="arrow"
            src={sortBy.isAscending ? Images.downArrow : Images.upArrow}
            onClick={() => updateSortBy('status')}
          />
        </TableHeading>
        <TableHeading minWidth="200px">
          Result
          <SortArrowImage
            alt="arrow"
            src={sortBy.isAscending ? Images.downArrow : Images.upArrow}
            onClick={() => updateSortBy('result')}
          />
        </TableHeading>
        <TableHeading>
          {isClearFilterVisible() && <ClearFilterText onClick={() => clearAllFilters()}>Clear filter</ClearFilterText>}
        </TableHeading>
      </TableRow>
    );
  };

  const renderTableBody = () => {
    return paginatedItems?.sort(sortFunc).map((ticket, index) => {
      let test = _.findWhere(testsAvailableForCompany, { _id: ticket.test });
      let ticketDate = ticket.completedAt || ticket.extendedDeadline || ticket.deadlineAt || ticket.createdAt;
      return (
        <TableRow
          hasHoverEffect
          isSelectedRow
          key={index}
          onClick={(event) => {
            if (['Not started', 'Started', 'Processing result'].includes(statusText(ticket))) {
              let ticketCopy = { ...ticket };
              ticketCopy.userData = {
                name: ticket.candidateName,
                email: ticket.candidateEmail,
              };
              setTicketModalInfo([ticketCopy]);
              setShowTicketLinkModal(true);
            } else if (event.metaKey || event.ctrlKey) {
              window.open(
                window.location.protocol + '//' + window.location.host + '/report/' + ticket._id + '/',
                '_blank'
              );
            } else {
              navigate(`/report/${ticket._id}/`);
            }
          }}
        >
          <TableData noBorder={true}>{ticket.candidateName}</TableData>
          {user.viewAll && (
            <TableData noBorder={true} hideOnSmallDevice hideOnMobileDevice>
              {ticket.companyName}
            </TableData>
          )}
          {user.role === userRoles.COMPANY_ADMIN && user.viewAllDepartments && (
            <TableData noBorder={true} hideOnSmallDevice hideOnMobileDevice>
              {ticket.departmentName}
            </TableData>
          )}
          <TableData noBorder={true} hideOnMobileDevice>
            {ticket.testName}

            {test?.difficulty && test?.category === categories.CODE.category && (
              <TestInformationText>Difficulty: {test.difficulty}</TestInformationText>
            )}
            {ticket.isCandidateChoosesEnabled === false && (
              <TestInformationText>Chosen by the candidate</TestInformationText>
            )}
          </TableData>
          <TableData noBorder={true} hideOnSmallDevice hideOnMobileDevice>
            {ticket.customerName}
          </TableData>
          <TableData noBorder={true} hideOnSmallDevice hideOnMobileDevice>
            {moment(ticketDate).format('YYYY-MM-DD')}
            <TimeBox>{moment(ticketDate).format('HH:mm')}</TimeBox>
          </TableData>
          <TableData noBorder={true} hideOnMobileDevice>
            <StatusLabel
              isProcessing={statusText(ticket) === 'Processing result'}
              isStarted={statusText(ticket) === 'Started'}
              isNotStarted={statusText(ticket) === 'Not started'}
              isFinished={statusText(ticket) === 'Finished'}
              data-tooltip-id="processing"
              data-tooltip-html={statusText(ticket) === 'Processing result' ? 'This might take a few minutes' : ''}
            >
              {statusText(ticket) === 'Processing result' ? (
                <>
                  <ITag className="fa fa-spinner fa-spin" usePadding="0" /> {statusText(ticket)}{' '}
                </>
              ) : (
                statusText(ticket)
              )}
            </StatusLabel>
          </TableData>
          <TableData noBorder={true}>
            <ResultBar ticket={ticket} test={test} />
          </TableData>
          <TableData noBorder={true} alignLeft>
            {statusText(ticket) === 'Started' &&
              test?.category === categories.CODE.category &&
              ticket.isInvitedToSpectate && (
                <RectangleButton
                  small
                  onClick={(event) => {
                    event.stopPropagation();
                    if (event.metaKey || event.ctrlKey) {
                      window.open(
                        window.location.protocol + '//' + window.location.host + '/freecode-live-coding/' + ticket._id,
                        '_blank'
                      );
                    } else {
                      navigate(`/freecode-live-coding/${ticket._id}/`);
                    }
                  }}
                >
                  View
                </RectangleButton>
              )}
            {statusText(ticket) === 'Finished' && (
              <ITag
                className="fa fa-download"
                color={colors.darkGray}
                usePadding="0 0 0 15px"
                onClick={async (event) => {
                  event.stopPropagation();
                  if (isPdfDownloading) return toast.error('Report already downloading');
                  setIsPdfDownloading(true);
                  await downloadReport(ticket._id, ticket.candidateName, ticket.testName, 'complete', false, false);
                  setIsPdfDownloading(false);
                }}
              />
            )}
            {['Finished', 'Not started', 'Processing result'].includes(statusText(ticket)) && (
              <ITag
                className="fa fa-times-circle"
                color={colors.darkGray}
                usePadding="0 15px 0 15px"
                onClick={(event) => {
                  event.stopPropagation();
                  setYesNoModalInfo({
                    title: 'Confirmation',
                    body: `Are you sure you want to archive the ${ticket.testName} of ${ticket.candidateName}?`,
                    archiveTicketId: ticket._id,
                  });
                  setShowYesNoModal(true);
                }}
              />
            )}
          </TableData>
        </TableRow>
      );
    });
  };

  return (
    <>
      {showYesNoModal && (
        <YesNoModal
          modalInfo={yesNoModalInfo}
          confirm={(isConfirmed) => {
            if (isConfirmed) {
              archiveTicket(yesNoModalInfo.archiveTicketId);
            }
            setShowYesNoModal(false);
            setYesNoModalInfo({});
          }}
        />
      )}
      {showTicketLinkModal && (
        <TicketLinkModal
          tickets={ticketModalInfo}
          closeModal={() => {
            setShowTicketLinkModal(false);
            setTicketModalInfo({});
          }}
        />
      )}
      <Table noBorder={true}>
        <TableHead>{renderTableHeading()}</TableHead>
        <TableBody>{renderTableBody()}</TableBody>
      </Table>
      <Tooltip id="processing" place="top" />
      {loading && <Spinner />}
      {ticketsList.length > itemsPerPage && (
        <Pagination
          key={version}
          items={ticketsList.sort(sortFunc)}
          itemsPerPage={itemsPerPage}
          getItems={setPaginatedItems}
          isForcedToReset={false}
          defaultPage={localStorageInfo?.pageNumber}
          updateLocalStorageInfo={updateLocalStorageInfo}
          extraPadding
        />
      )}
    </>
  );
}
